import { useAuth } from "@/app/providers/AuthProvider";
import toast from 'react-hot-toast';
import { Button, FormField } from "@/components";
import {
    LogOut,
    Save,
    Loader2,
    Bell,
    Shield,
    Volume2,
    Vibrate,
    MessageSquare,
    AlertTriangle,
    RefreshCw,
    CheckCircle2,
    XCircle,
    Info
} from "lucide-react";
import React, { useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import { useUpdateProfile } from "../hooks/useSettingQuery";
import { Formik, Form, Field, FieldProps, ErrorMessage } from "formik";
import * as Yup from 'yup';
import { usePushNotifications } from "@/hooks/usePushNotifications";
import { NotificationTopics, NotificationSettings as NotificationSettingsType } from "@/types/notifications";
import { cn } from "@/lib/utils";
import PhoneInput from 'react-phone-number-input';
import 'react-phone-number-input/style.css';

// Validation Schema for Profile
const profileValidationSchema = Yup.object({
    firstName: Yup.string()
        .required('First name is required')
        .min(2, 'First name must be at least 2 characters')
        .max(50, 'First name must be less than 50 characters')
        .matches(/^[a-zA-Z]+$/, 'First name can only contain letters without spaces'),
    lastName: Yup.string()
        .required('Last name is required')
        .min(2, 'Last name must be at least 2 characters')
        .max(50, 'Last name must be less than 50 characters')
        .matches(/^[a-zA-Z]+$/, 'Last name can only contain letters without spaces'),
    phone: Yup.string()
        .required('Phone number is required')
        .test('is-valid-phone', 'Please enter a valid phone number', (value) => {
            if (!value) return false;
            return value.length >= 10;
        }),
    email: Yup.string()
        .email('Please enter a valid email address')
        .required('Email is required'),
    companyName: Yup.string()
        .trim()
        .max(100, 'Company name must be less than 100 characters')
        .nullable(),
    gstNumber: Yup.string()
        .trim()
        .nullable()
        .notRequired()
        .test(
            'empty-or-valid-gst',
            'Please enter a valid GST number',
            (value) =>
                !value ||
                /^[0-9]{2}[A-Z]{5}[0-9]{4}[A-Z]{1}[1-9A-Z]{1}Z[0-9A-Z]{1}$/.test(
                    value.toUpperCase()
                )
        ),
    address: Yup.string()
        .trim()
        .max(250, 'Address must be less than 250 characters')
        .nullable(),
});

// Notification Settings Section Component
const NotificationSection = ({ userId }: { userId?: string }) => {
    const {
        permissionState,
        isLoading,
        settings,
        enableNotifications,
        disableNotifications,
        updateSettings,
        isSupported,
        isEnabled,
        initialize
    } = usePushNotifications(userId);

    const [localSettings, setLocalSettings] = useState<NotificationSettingsType>(settings);

    // Sync local settings with hook settings when they change
    React.useEffect(() => {
        setLocalSettings(settings);
    }, [settings]);

    const handleTopicChange = (topic: NotificationTopics, enabled: boolean) => {
        const newSettings = {
            ...localSettings,
            topics: {
                ...localSettings.topics,
                [topic]: enabled
            }
        };
        setLocalSettings(newSettings);
        updateSettings(newSettings);
    };

    const handleToggleChange = (key: 'sound' | 'vibrate', enabled: boolean) => {
        const newSettings = {
            ...localSettings,
            [key]: enabled
        };
        setLocalSettings(newSettings);
        updateSettings(newSettings);
    };

    const handleEnableNotifications = async () => {
        await enableNotifications();
        await initialize()
    };

    const topicLabels: Record<NotificationTopics, { label: string; description: string; icon: React.ReactNode }> = {
        [NotificationTopics.GENERAL]: {
            label: 'General Notifications',
            description: 'Updates about your account and activity',
            icon: <Bell size={32} className="text-gray-400 rounded bg-gray-50 dark:bg-gray-700/80 p-2" />
        },
        [NotificationTopics.ALERTS]: {
            label: 'Important Alerts',
            description: 'Critical alerts requiring immediate attention',
            icon: <AlertTriangle size={32} className="text-gray-400 rounded bg-gray-50 dark:bg-gray-700/80 p-2" />
        },
        [NotificationTopics.UPDATES]: {
            label: 'App Updates',
            description: 'New features and improvements',
            icon: <RefreshCw size={32} className="text-gray-400 rounded bg-gray-50 dark:bg-gray-700/80 p-2" />
        },
        [NotificationTopics.CHAT]: {
            label: 'Chat Messages',
            description: 'New messages from conversations',
            icon: <MessageSquare size={32} className="text-gray-400 rounded bg-gray-50 dark:bg-gray-700/80 p-2" />
        },
        [NotificationTopics.SYSTEM]: {
            label: 'System Notifications',
            description: 'System maintenance and status updates',
            icon: <Info size={32} className="text-gray-400 rounded bg-gray-50 dark:bg-gray-700/80 p-2" />
        }
    };

    const isIOS = /iPad|iPhone|iPod/.test(navigator.userAgent) || ('maxTouchPoints' in navigator && navigator.maxTouchPoints > 1 && /Macintosh/.test(navigator.userAgent));
    const isStandalone = window.matchMedia('(display-mode: standalone)').matches || (navigator as any).standalone === true;

    if (!isSupported) {
        return (
            <div className="bg-amber-50 dark:bg-amber-900/20 border border-amber-200 dark:border-amber-800 rounded-lg p-4">
                <div className="flex items-start gap-3">
                    <Info className="w-5 h-5 text-amber-600 dark:text-amber-400 mt-0.5" />
                    <div>
                        <h4 className="font-medium text-amber-900 dark:text-amber-100">Notifications Not Supported</h4>
                        {isIOS && !isStandalone ? (
                            <div className="text-sm text-amber-700 dark:text-amber-300 mt-1 space-y-2">
                                <p>
                                    Push notifications on iPhone/iPad require adding this app to your Home Screen.
                                </p>
                                <ol className="list-decimal list-inside space-y-1">
                                    <li>Tap the <strong>Share</strong> button (square with arrow) in Safari</li>
                                    <li>Scroll down and tap <strong>"Add to Home Screen"</strong></li>
                                    <li>Open the app from your Home Screen</li>
                                    <li>Come back to this page to enable notifications</li>
                                </ol>
                                <p className="text-xs mt-2">
                                    Note: Requires iOS 16.4 or later.
                                </p>
                            </div>
                        ) : (
                            <p className="text-sm text-amber-700 dark:text-amber-300 mt-1">
                                Push notifications are not supported in your browser. Please use a modern browser like Chrome, Firefox, or Safari.
                            </p>
                        )}
                    </div>
                </div>
            </div>
        );
    }

    return (
        <div className="space-y-6">
            {/* Status Card */}
            <div className={cn(
                "rounded-lg p-3 lg:p-4 border",
                isEnabled
                    ? "bg-green-50 dark:bg-green-900/20 border-green-200 dark:border-green-800"
                    : "bg-gray-50 dark:bg-gray-800/50 border-gray-200 dark:border-gray-700"
            )}>
                <div className="flex flex-col lg:flex-row gap-4 items-center justify-between">
                    <div className="flex items-center gap-3">
                        <div className={cn(
                            "w-10 h-10 min-w-10 rounded-sm flex items-center justify-center",
                            isEnabled
                                ? "bg-green-100 dark:bg-green-900/50 text-green-600 dark:text-green-400"
                                : "bg-gray-200 dark:bg-gray-700 text-gray-500 dark:text-gray-300"
                        )}>
                            {isEnabled ? <CheckCircle2 className="w-5 h-5" /> : <Bell className="w-5 h-5" />}
                        </div>
                        <div>
                            <h4 className="font-medium text-gray-900 dark:text-gray-100 text-sm lg:text-base">
                                {isEnabled ? 'Notifications Enabled' : 'Notifications Disabled'}
                            </h4>
                            <p className="text-xs lg:text-sm text-gray-500 dark:text-gray-300">
                                {isEnabled
                                    ? 'You will receive push notifications for selected topics'
                                    : 'Enable notifications to stay updated on important events'}
                            </p>
                        </div>
                    </div>
                    {isEnabled ? (
                        <Button
                            variant="outline"
                            size="sm"
                            onClick={disableNotifications}
                            disabled={isLoading}
                            className="ms-auto text-red-600 border-red-200 bg-red-50  hover:text-red-500 hover:border-red-500 dark:hover:text-red-500 dark:hover:border-red-500 dark:text-red-500 dark:bg-red-500/20 dark:border-red-600/60 "
                            testId="setting-disable-notifications-button"
                        >
                            {isLoading ? <Loader2 className="w-4 h-4 animate-spin mr-2" /> : <XCircle className="w-4 h-4 mr-2" />}
                            Disable
                        </Button>
                    ) : (
                        <Button
                            size="sm"
                            onClick={handleEnableNotifications}
                            disabled={isLoading || permissionState.permission === 'denied'}
                            className="ms-auto"
                            testId="setting-enable-notifications-button"
                        >
                            {isLoading ? <Loader2 className="w-4 h-4 animate-spin mr-2" /> : <Bell className="w-4 h-4 mr-2" />}
                            Enable
                        </Button>
                    )}
                </div>
                {permissionState.permission === 'denied' && (
                    <div className="mt-3 p-3 bg-amber-100 dark:bg-amber-900/30 rounded-lg">
                        <p className="text-sm text-amber-800 dark:text-amber-200">
                            Notification permission was denied. Please enable it in your browser settings to receive notifications.
                        </p>
                    </div>
                )}
            </div>

            {/* Preferences */}
            {isEnabled && (
                <div className="space-y-6">
                    {/* General Preferences */}
                    <div className="bg-white dark:bg-[#272E3E] rounded-lg border border-gray-200 dark:border-gray-700 overflow-hidden">
                        <div className="px-4 py-3 border-b border-gray-200 dark:border-gray-700 bg-gray-50/50 dark:bg-gray-800/50 flex items-center gap-3">
                            <Shield size={32} className="rounded text-blue-500 p-2 bg-blue-50 dark:bg-blue-500/20" />
                            <div>
                                <h4 className="font-medium text-gray-900 dark:text-gray-100 flex items-center gap-2 text-sm lg:text-base">
                                    Preferences
                                </h4>
                                <p className="text-gray-700 dark:text-gray-400 text-[10px] lg:text-xs font-normal">Customize your notification preferences</p>
                            </div>
                        </div>
                        <div className="p-4 space-y-4">
                            <div className="flex items-center justify-between">
                                <div className="flex items-center gap-3">
                                    <Volume2 size={32} className="text-gray-400 rounded bg-gray-50 dark:bg-gray-700/80 p-2" />
                                    <div>
                                        <span className="text-sm font-medium text-gray-700 dark:text-gray-300">Sound</span>
                                        <p className="text-xs text-gray-500 dark:text-gray-300">Play sound for notifications</p>
                                    </div>
                                </div>
                                <label className="relative inline-flex items-center cursor-pointer">
                                    <input
                                        type="checkbox"
                                        className="sr-only peer"
                                        checked={localSettings.sound}
                                        onChange={(e) => handleToggleChange('sound', e.target.checked)}
                                    />
                                    <div className="w-11 h-6 bg-gray-200 peer-focus:outline-none peer-focus:ring-4 peer-focus:ring-blue-300 dark:peer-focus:ring-blue-800 rounded-full peer  peer-checked:after:translate-x-full peer-checked:after:border-white after:content-[''] after:absolute after:top-[2px] after:left-[2px] after:bg-white after:border-gray-300 after:border after:rounded-full after:h-5 after:w-5 after:transition-all dark:border-gray-600 peer-checked:bg-blue-600"></div>
                                </label>
                            </div>
                            <div className="flex items-center justify-between">
                                <div className="flex items-center gap-3">
                                    <Vibrate size={32} className="text-gray-400 rounded bg-gray-50 dark:bg-gray-700/80 p-2" />
                                    <div>
                                        <span className="text-sm font-medium text-gray-700 dark:text-gray-300">Vibration</span>
                                        <p className="text-xs text-gray-500 dark:text-gray-300">Vibrate device for notifications</p>
                                    </div>
                                </div>
                                <label className="relative inline-flex items-center cursor-pointer">
                                    <input
                                        type="checkbox"
                                        className="sr-only peer"
                                        checked={localSettings.vibrate}
                                        onChange={(e) => handleToggleChange('vibrate', e.target.checked)}
                                    />
                                    <div className="w-11 h-6 bg-gray-200 peer-focus:outline-none peer-focus:ring-4 peer-focus:ring-blue-300 dark:peer-focus:ring-blue-800 rounded-full peer  peer-checked:after:translate-x-full peer-checked:after:border-white after:content-[''] after:absolute after:top-[2px] after:left-[2px] after:bg-white after:border-gray-300 after:border after:rounded-full after:h-5 after:w-5 after:transition-all dark:border-gray-600 peer-checked:bg-blue-600"></div>
                                </label>
                            </div>
                        </div>
                    </div>

                    {/* Topic Preferences */}
                    <div className="bg-white dark:bg-[#272E3E] rounded-lg border border-gray-200 dark:border-gray-700 overflow-hidden">
                        <div className="px-4 py-3 border-b border-gray-200 dark:border-gray-700 bg-gray-50/50 dark:bg-gray-800/50 flex items-center gap-3">
                            <Bell size={32} className="rounded text-blue-500 p-2 bg-blue-50 dark:bg-blue-500/20" />
                            <div>
                                <h4 className="font-medium text-gray-900 dark:text-gray-100 flex items-center gap-2 text-sm lg:text-base">
                                    Notification Topics
                                </h4>
                                <p className="text-gray-700 dark:text-gray-400 text-[10px] lg:text-xs font-normal">Choose which types of notifications you want to receive</p>
                            </div>
                        </div>
                        <div className="p-4 space-y-3">
                            {Object.entries(NotificationTopics).map(([, topic]) => (
                                <div key={topic} className="flex items-center justify-between py-2">
                                    <div className="flex items-center gap-3">
                                        <span className="text-gray-400">
                                            {topicLabels[topic].icon}
                                        </span>
                                        <div>
                                            <span className="text-sm font-medium text-gray-700 dark:text-gray-300">
                                                {topicLabels[topic].label}
                                            </span>
                                            <p className="text-xs text-gray-500 dark:text-gray-300">
                                                {topicLabels[topic].description}
                                            </p>
                                        </div>
                                    </div>
                                    <label className="relative inline-flex items-center cursor-pointer">
                                        <input
                                            type="checkbox"
                                            className="sr-only peer"
                                            checked={localSettings.topics[topic]}
                                            onChange={(e) => handleTopicChange(topic, e.target.checked)}
                                        />
                                        <div className="w-11 h-6 bg-gray-200 peer-focus:outline-none peer-focus:ring-4 peer-focus:ring-blue-300 dark:peer-focus:ring-blue-800 rounded-full peer  peer-checked:after:translate-x-full peer-checked:after:border-white after:content-[''] after:absolute after:top-[2px] after:left-[2px] after:bg-white after:border-gray-300 after:border after:rounded-full after:h-5 after:w-5 after:transition-all dark:border-gray-600 peer-checked:bg-blue-600"></div>
                                    </label>
                                </div>
                            ))}
                        </div>
                    </div>
                </div>
            )}
        </div>
    );
};

interface SettingProps {
    hideHeader?: boolean;
    hideInternalFooter?: boolean;
    mode?: 'profile' | 'notifications' | 'all';
    isSaving?: boolean;
    onClose?: () => void;
}

const Setting = ({ hideHeader = false, hideInternalFooter = false, mode = 'all', isSaving = false,onClose }: SettingProps) => {
    const navigate = useNavigate();
    const {id: chatbotId} = useParams();
    const { user, logout ,initializeAuth} = useAuth();
    const [isLoggingOut, setIsLoggingOut] = useState(false);
    const { mutate: updateProfile, isPending: isUpdatingInternal } = useUpdateProfile();
    const isOwner = !!user?.access?.isOwner;

    const isUpdating = isSaving || isUpdatingInternal;

    const handleLogout = async () => {
        setIsLoggingOut(true);
        await logout();
        navigate('/login');
        setIsLoggingOut(false);
    };



    const handleSubmit = (values: any) => {
        const payload = {
            ...values,
            companyName: values.companyName?.trim() || undefined,
            gstNumber: values.gstNumber?.trim().toUpperCase() || undefined,
            address: values.address?.trim() || undefined,
        };

        updateProfile(payload, {
            onSuccess: (data) => {
                toast.success(data.message);
                initializeAuth(chatbotId!);
                onClose?.()
            }
        });
    };

    const handleKeyDown = (e: React.KeyboardEvent<HTMLInputElement | HTMLTextAreaElement>) => {
        if (e.key === ' ') {
            e.preventDefault();
        }
    };

    return (
        <div className={cn(!hideHeader && "container-full py-6 mx-auto w-full")} data-testid="setting-page">
            {/* Header */}
            {!hideHeader && (
                <div className="flex flex-col md:flex-row justify-between items-start md:items-center mb-6 gap-4">
                    <div>
                        <h1 className="text-3xl font-bold tracking-tight text-dark dark:text-white" data-testid="setting-title">
                            Account Settings
                        </h1>
                        <p className="text-gray-600 dark:text-gray-300 mt-1">
                            Manage your account settings and preferences
                        </p>
                    </div>

                    <button
                        className="ms-auto flex font-medium items-center gap-2 px-4 border border-red-600 py-2 text-red-600 bg-red-50 hover:bg-red-100 dark:bg-red-900/20 dark:hover:bg-red-900/30 rounded-sm cursor-pointer transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
                        onClick={handleLogout}
                        disabled={isLoggingOut}
                        data-testid="setting-logout-button"
                    >
                        {isLoggingOut ? <Loader2 size={16} className="animate-spin" /> : <LogOut size={16} />}
                        {isLoggingOut ? 'Logging out...' : 'Logout'}
                    </button>
                </div>
            )}

            {/* Settings Grid */}
            <div className="grid grid-cols-1 gap-6">

                {/* Account Settings Section */}
                {(mode === 'profile' || mode === 'all') && (
                    <div className="flex flex-col gap-4">
                        <Formik
                            onSubmit={handleSubmit}
                            initialValues={{
                                firstName: user?.user?.firstName || '',
                                lastName: user?.user?.lastName || '',
                                phone: user?.user?.phone || '',
                                email: user?.user?.email || '',
                                companyName: user?.user?.companyName || '',
                                gstNumber: user?.user?.gstNumber || '',
                                address: user?.user?.address || '',
                            }}
                            validationSchema={profileValidationSchema}
                            enableReinitialize
                        >
                            <Form id="profile-form" className="flex flex-col gap-2" data-testid="setting-form">
                                <div className="grid grid-cols-1 lg:grid-cols-2 gap-x-4">
                                    <FormField
                                        name="firstName"
                                        label="First Name"
                                        type="text"
                                        placeholder="Enter your first name"
                                        onKeyDown={handleKeyDown}
                                        testId="setting-first-name-input"
                                    />
                                    <FormField
                                        name="lastName"
                                        label="Last Name"
                                        type="text"
                                        placeholder="Enter your last name"
                                        onKeyDown={handleKeyDown}
                                        testId="setting-last-name-input"
                                    />

                                    <div className="flex flex-col gap-1">
                                        <label className="text-sm font-medium text-gray-700 dark:text-gray-300">
                                            Phone Number
                                        </label>
                                        <Field name="phone">
                                            {({ field, form }: FieldProps) => (
                                                <PhoneInput
                                                    international
                                                    countryCallingCodeEditable={false}
                                                    defaultCountry="AU"
                                                    value={field.value}
                                                    onChange={(value) => form.setFieldValue('phone', value || '')}
                                                    className={cn(
                                                        "flex h-10 w-full rounded-md border border-gray-300 bg-white px-3 py-2 text-sm",
                                                        "focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent",
                                                        "disabled:cursor-not-allowed disabled:opacity-50",
                                                        "dark:border-gray-600 dark:bg-gray-700 dark:text-white",
                                                        form.errors.phone && form.touched.phone && "border-red-500 focus:ring-red-500"
                                                    )}
                                                />
                                            )}
                                        </Field>
                                        <ErrorMessage name="phone" component="p" className="text-sm text-red-500" />
                                    </div>
                                    <FormField
                                        name="email"
                                        label="Email Address"
                                        type="email"
                                        placeholder="Enter your email address"
                                        disabled
                                        testId="setting-email-input"
                                    />

                                    {isOwner && (
                                        <>
                                            <FormField
                                                name="companyName"
                                                label="Company Name"
                                                type="text"
                                                placeholder="Enter your company name"
                                                testId="setting-company-name-input"
                                            />
                                            <FormField
                                                name="gstNumber"
                                                label="GST Number"
                                                type="text"
                                                placeholder="Enter your GST number"
                                                testId="setting-gst-number-input"
                                            />
                                            <div className="lg:col-span-2">
                                                <FormField
                                                    name="address"
                                                    label="Address"
                                                    as="textarea"
                                                    rows={3}
                                                    placeholder="Enter your business address"
                                                    testId="setting-address-input"
                                                />
                                            </div>
                                        </>
                                    )}
                                </div>

                                {!hideInternalFooter && (
                                    <div className="flex justify-end">
                                        <Button
                                            type="submit"
                                            disabled={isUpdating}
                                            className="min-w-[140px]"
                                            testId="setting-save-button"
                                        >
                                            {isUpdating ? <Loader2 className="mr-2 h-4 w-4 animate-spin" /> : <Save className="mr-2 h-4 w-4" />}
                                            {isUpdating ? 'Saving...' : 'Save'}
                                        </Button>
                                    </div>
                                )}
                            </Form>
                        </Formik>
                    </div>
                )}

                {/* Notification Settings Section */}
                {(mode === 'notifications' || mode === 'all') && (

                    <NotificationSection userId={user?.user?.id} />
                )}
            </div>
        </div>
    );
};

export default Setting;
